import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadGoogleMapsScript } from './maps_loader';
import { HIGH_VISIBILITY_MAP_STYLE } from './map_styles';

const USA_CENTER = { lat: 39.8283, lng: -98.5795 };
const MAX_MAP_LOCATIONS = 1000;
export const LOCATION_MARKER_ICON = {
  path: 'M 0 0 C -2 -4 -10 -12 -10 -20 C -10 -26 -5 -31 0 -31 C 5 -31 10 -26 10 -20 C 10 -12 2 -4 0 0 Z',
  fillColor: '#5277ad',
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeOpacity: 1,
  strokeWeight: 2,
  scale: 1.1
};
const valueFor = (record, field) => record && record[field] ? record[field].value : '';
export const isInactiveLocation = record => {
  const value = valueFor(record, 50);
  return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
};

export const formatLocationAddress = value => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  return [value.street, value.street2, value.city, value.region, value.postal, value.country]
    .filter(Boolean).join(', ');
};

export const mapLocationRecord = record => {
  const id = String(valueFor(record, 3) || '');
  const rawLatitude = valueFor(record, 134);
  const rawLongitude = valueFor(record, 135);
  const latitude = Number(rawLatitude);
  const longitude = Number(rawLongitude);
  return {
    id,
    portalApp: String(valueFor(record, '_portalApp') || 'waste'),
    name: String(valueFor(record, 7) || valueFor(record, 66) || '').trim(),
    code: String(valueFor(record, 35) || '').trim(),
    address: formatLocationAddress(valueFor(record, 8)),
    position: rawLatitude !== '' && rawLongitude !== '' && Number.isFinite(latitude) && Number.isFinite(longitude) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180
      ? { lat: latitude, lng: longitude }
      : null
  };
};

const buildInfoContent = location => {
  const wrapper = document.createElement('div');
  wrapper.className = 'mapInfoWindow';
  const title = document.createElement('strong');
  title.textContent = [location.name, location.code].filter(Boolean).join(' – ') || 'Location';
  const address = document.createElement('div');
  address.textContent = location.address;
  wrapper.append(title, address);
  return wrapper;
};

export const ClientLocationsMap = () => {
  const mapNode = useRef(null);
  const locationsRequest = useRef(null);
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', locations: [], error: '' });
  const [mapLoading, setMapLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!locationsRequest.current) {
      locationsRequest.current = fetch('/apilocations').then(response => {
        if (!response.ok) throw new Error('Unable to load client locations');
        return response.json();
      });
    }
    locationsRequest.current
      .then(records => {
        if (cancelled) return;
        const locations = Array.isArray(records) ? records.filter(record => !isInactiveLocation(record)).map(mapLocationRecord) : [];
        const seen = new Set();
        const unique = locations.filter(location => {
          const key = `${location.portalApp}:${location.id || location.address.toLowerCase()}`;
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return Boolean(location.position);
        }).slice(0, MAX_MAP_LOCATIONS);
        setState({ status: unique.length ? 'ready' : 'empty', locations: unique, error: '' });
      })
      .catch(error => !cancelled && setState({ status: 'error', locations: [], error: error.message }));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (state.status !== 'ready' || !mapNode.current) return;
    let cancelled = false;
    let markers = [];
    const initialize = async () => {
      try {
        await loadGoogleMapsScript();
        if (cancelled) return;
        const map = new window.google.maps.Map(mapNode.current, {
          center: USA_CENTER,
          zoom: 4,
          styles: HIGH_VISIBILITY_MAP_STYLE
        });
        const infoWindow = new window.google.maps.InfoWindow();
        const addMarker = (location, position) => {
          if (cancelled || !position) return;
          const marker = new window.google.maps.Marker({
            map,
            position,
            title: [location.name, location.code].filter(Boolean).join(' – '),
            icon: LOCATION_MARKER_ICON
          });
          marker.addListener('mouseover', () => {
            infoWindow.setContent(buildInfoContent(location));
            infoWindow.open({ map, anchor: marker });
          });
          marker.addListener('mouseout', () => infoWindow.close());
          if (location.id) marker.addListener('click', () => navigate(`/location/${location.id}${location.portalApp === 'fire_pest' ? '?app=fire_pest' : ''}`, { state: { id: location.id } }));
          markers.push(marker);
        };
        state.locations.forEach(location => addMarker(location, location.position));
        if (!cancelled) setMapLoading(false);
      } catch (error) {
        if (!cancelled) setState(current => ({ ...current, status: 'error', error: error.message }));
      }
    };
    initialize();
    return () => {
      cancelled = true;
      markers.forEach(marker => marker.setMap(null));
      markers = [];
    };
  }, [state.status, state.locations, navigate]);

  return (
    <section className="mapCard" aria-labelledby="locations-map-title">
      <div className="mapCardHeader">
        <div><h2 id="locations-map-title">Client locations</h2><p>Active locations available to your account</p></div>
      </div>
      {state.status === 'loading' && <div className="mapState" role="status">Loading locations…</div>}
      {state.status === 'empty' && <div className="mapState">No active locations are available.</div>}
      {state.status === 'error' && <div className="mapState mapError" role="alert">Map unavailable — {state.error}</div>}
      <div className={`mapCanvasWrapper ${state.status === 'ready' ? '' : 'mapHidden'}`}>
        {mapLoading && <div className="mapState mapStateOverlay" role="status">Loading map…</div>}
        <div ref={mapNode} className="locationsMap" aria-label="Map of client locations" />
      </div>
    </section>
  );
};
